/**
 * Institution Dashboard — tenant-scoped landing.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function InstitutionDashboard() {
  const { user } = useAuth();
  const instId = user?.institution_id;
  const [profile, setProfile] = useState(null);
  const [cases, setCases] = useState([]);
  const [audit, setAudit] = useState([]);
  const [cap, setCap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!instId) { setLoading(false); return; }
    Promise.allSettled([
      api.get(`/institutions/${instId}`),
      // Note (2026-02-21 sanitation): platform-wide /analytics/overview
      // was previously called here — it returned VeraLok-wide identity,
      // audit and institution counts to any institution_admin. That is
      // a tenant boundary violation. The Institution Portal now derives
      // its own KPIs from tenant-scoped endpoints only.
      api.get(`/cases?limit=10`),
      api.get(`/audit?limit=20`),
      api.get(`/cap/institution/${instId}`),
    ]).then(([p, c, au, cp]) => {
      if (p.status === "fulfilled") setProfile(p.value.data);
      if (c.status === "fulfilled") setCases(c.value.data?.items || []);
      if (au.status === "fulfilled") setAudit(au.value.data?.items || []);
      if (cp.status === "fulfilled") setCap(cp.value.data);
      setLoading(false);
    }).catch((e) => {
      setErr(e.message);
      setLoading(false);
    });
  }, [instId]);

  if (!instId) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="institution-dashboard">
        This account is not bound to an institution. Contact your VeraLok administrator to attach a tenant.
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-muted-foreground text-sm" data-testid="institution-dashboard-loading">Loading dashboard…</div>;
  }

  const openCases = cases.filter((c) => c.status === "OPEN" || c.status === "IN_REVIEW").length;

  return (
    <div className="space-y-6" data-testid="institution-dashboard">
      <div>
        <h1 className="text-xl font-semibold">Institution Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Tenant: <code className="text-xs bg-muted px-1 py-0.5 rounded">{instId}</code>
          {profile?.name && <> · {profile.name}</>}
        </p>
      </div>

      {err && (
        <div className="border border-destructive/40 bg-destructive/5 rounded p-3 text-xs" data-testid="institution-dashboard-error">
          {err}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Status" value={profile?.status || "?"} testid="kpi-status"
             tone={profile?.status === "APPROVED" ? "ok" : "warn"} />
        <Kpi label="Open cases" value={openCases} testid="kpi-cases" />
        <Kpi label="Active credentials" value={profile?.counters?.credentials_active ?? 0} testid="kpi-creds" />
        <Kpi label="24h calls" value={profile?.usage_24h?.total_calls ?? 0} testid="kpi-calls" />
      </div>

      {cap && (
        <div className="border rounded p-4" data-testid="institution-dashboard-cap">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Compliance</div>
              <div className="text-sm font-medium">{cap.status}</div>
            </div>
            <Link to="/institution/compliance" className="text-xs underline underline-offset-4">Manage →</Link>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border rounded p-4" data-testid="institution-dashboard-cases">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Recent cases</div>
            <Link to="/institution/cases" className="text-xs underline underline-offset-4">All →</Link>
          </div>
          {cases.length === 0
            ? <div className="text-xs text-muted-foreground">No cases yet.</div>
            : (
              <ul className="text-xs space-y-1">
                {cases.slice(0, 5).map((c) => (
                  <li key={c.case_id} className="flex justify-between gap-2">
                    <span className="truncate">{c.title || c.case_id}</span>
                    <span className="uppercase tracking-widest text-muted-foreground">{c.status}</span>
                  </li>
                ))}
              </ul>
            )}
        </div>

        <div className="border rounded p-4" data-testid="institution-dashboard-audit">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Recent audit</div>
            <Link to="/institution/audit" className="text-xs underline underline-offset-4">All →</Link>
          </div>
          {audit.length === 0
            ? <div className="text-xs text-muted-foreground">No audit events yet.</div>
            : (
              <ul className="text-xs space-y-1">
                {audit.slice(0, 5).map((a) => (
                  <li key={a.ledger_id || a._id} className="flex justify-between gap-2">
                    <span className="truncate font-mono text-[10px]">{a.action}</span>
                    <span className="text-muted-foreground">{(a.timestamp || "").slice(11, 19)}</span>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>

      {/* 7-day analytics snapshot removed 2026-02-21 — it pulled
          platform-wide values from /analytics/overview and misled the
          institution operator into thinking the numbers were their own.
          Tenant-scoped verification/consent/lock counters live in the
          per-workflow pages (Verifications, Consents, Cases). */}
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  const cls = tone === "ok" ? "text-green-600 dark:text-green-500"
                            : tone === "warn" ? "text-yellow-600 dark:text-yellow-500" : "";
  return (
    <div className="border rounded p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${cls}`}>{String(value)}</div>
    </div>
  );
}

function Small({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono">{String(value)}</div>
    </div>
  );
}
